/**
 * Form Readiness Audit — pre-submission quality-control engine.
 *
 * One pure function (`runFormAudit`) inspects the 526EZ master form, the
 * 21-0781 worksheet, the workspace's claimed conditions, and the attached
 * evidence to produce a deterministic, stable list of findings. Findings
 * are sorted by severity so the UI can render the worst stuff first.
 *
 * Severity ladder (worst → best):
 *   • critical → "Missing Critical Info"        (blocks submission)
 *   • confusion → "Possible Claim Confusion"    (wrong field, wrong place)
 *   • review → "Needs Review"                   (ambiguous, may be ok)
 *   • ready → "Ready"                           (passed check, no action)
 *
 * Every finding has:
 *   { id, severity, title, why, fix }
 *
 * Tested separately in __tests__/formAudit.test.js — DO NOT add UI
 * dependencies here.
 */
import { runCrossFormAudit } from "./crossFormConsistency";


// ---------------------------------------------------------------------------
// SEVERITY LABELS (user-facing). Single source of truth for the panel.
// ---------------------------------------------------------------------------
export const AUDIT_STATUS = {
  critical:  "Missing Critical Info",
  confusion: "Possible Claim Confusion",
  review:    "Needs Review",
  ready:     "Ready",
};

const SEVERITY_ORDER = { critical: 0, confusion: 1, review: 2, ready: 3 };

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

// Conditions that DO NOT belong on 21-0781's "mental health conditions" field.
// These are physical conditions sometimes confused with mental-health symptoms.
const NON_MENTAL_DISORDER_PATTERNS = [
  { rx: /\bsleep\s*apnea\b/i,           label: "sleep apnea" },
  { rx: /\bosa\b/i,                     label: "OSA (sleep apnea)" },
  { rx: /\btinnitus\b/i,                label: "tinnitus" },
  { rx: /\bmigraine\b/i,                label: "migraine" },
  { rx: /\bhypertens(ion|ive)\b/i,      label: "hypertension" },
];

// "Generic" condition names that almost always need side / joint / level
// specificity before VA will rate them. Veterans frequently file claims as
// "back pain" or "arm limitation of motion" — raters need to know WHICH
// joint, WHICH side, WHICH level.
const GENERIC_CONDITION_PATTERNS = [
  // <body part> + limitation of motion / pain / strain — requires side+joint
  { rx: /^(arm|leg|hand|foot|knee|shoulder|elbow|wrist|ankle|hip)\s+(limitation of motion|pain|strain|injury|condition)\b/i,
    issue: "Needs side (left / right) and a specific joint." },
  // "Back pain" alone — needs spinal level
  { rx: /^back\s+(pain|injury|problem|condition)\b/i,
    issue: "Needs spinal level (cervical / thoracic / lumbar) and the specific diagnosis (strain, DDD, IVDS, radiculopathy)." },
  // Just a body part as the entire condition name
  { rx: /^(shoulder|knee|ankle|wrist|elbow|hip|foot|hand|eye|ear)\s*(pain)?$/i,
    issue: "Too generic — add side (left / right) and the specific diagnosis." },
  // "Mental health" / "anxiety" with no descriptor
  { rx: /^(mental health|nerves|stress)$/i,
    issue: "Too generic — name the specific diagnosis (PTSD, GAD, MDD, adjustment disorder, etc.)." },
];

// Reasonable bounds for service-date sanity checks
const MIN_REASONABLE_YEAR = 1940;
const MAX_REASONABLE_AGE_AT_ENTRY_DAYS = 365 * 50; // entry past age 50 from DOB is unusual but allowed
const MIN_SERVICE_DAYS = 1;
const MAX_SERVICE_DAYS = 365 * 50; // 50 years total is the cap

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function dateOk(s) {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function norm(s) {
  return (s || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function add(findings, finding) {
  findings.push(finding);
}

// ---------------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------------

/**
 * @param {Object} args
 * @param {Object} args.master526ez   Required.
 * @param {Object} args.form0781      Optional (mental health worksheet).
 * @param {Array}  args.issues        Claim Tracker issues.
 * @param {Array}  args.evidence      Attached evidence items.
 * @param {Object} args.profile       Workspace profile.
 * @param {Object} args.workspace     Workspace meta (used for claim type).
 */
export function runFormAudit({
  master526ez = {},
  form0781 = {},
  issues = [],
  evidence = [],
  statements = [],
  profile = {},
  workspace = {},
} = {}) {
  const findings = [];

  // -------------------------------------------------------------------------
  // 1. VETERAN NAME MISMATCH ACROSS FORMS
  // -------------------------------------------------------------------------
  const names = {
    "Master 21-526EZ": (master526ez.veteranName || "").trim(),
    "VA Form 21-0781": (form0781.veteranName || "").trim(),
    "Workspace profile": (profile.name || "").trim(),
  };
  const distinctNames = Array.from(new Set(Object.values(names).filter(Boolean).map((n) => n.toLowerCase())));
  if (distinctNames.length === 0) {
    add(findings, {
      id: "name-missing",
      severity: "critical",
      title: "Veteran name not set",
      why: "VA matches every form, statement, and exhibit to your file by name. A missing name blocks the entire packet.",
      fix: "Enter your full legal name on the 526EZ Veteran Name field.",
    });
  } else if (distinctNames.length > 1) {
    const where = Object.entries(names).filter(([, v]) => v).map(([k, v]) => `${k}: "${v}"`).join(" • ");
    add(findings, {
      id: "name-mismatch",
      severity: "confusion",
      title: "Veteran name doesn't match across forms",
      why: "VA raters cross-check identity across every submitted form. Even a punctuation or initial difference triggers a verification delay.",
      fix: `Pick one canonical name (matching your DD-214) and use it everywhere. Current values: ${where}`,
    });
  } else {
    add(findings, {
      id: "name-ok",
      severity: "ready",
      title: "Veteran name consistent",
      why: "Identity matches across the 526EZ, 21-0781, and workspace profile.",
      fix: null,
    });
  }

  // -------------------------------------------------------------------------
  // 2. SERVICE LENGTH / SUSPICIOUS DATES
  // -------------------------------------------------------------------------
  const startD = dateOk(master526ez.serviceStart);
  const endD = dateOk(master526ez.serviceEnd);
  const dobD = dateOk(master526ez.dateOfBirth);
  if (!startD || !endD) {
    add(findings, {
      id: "service-dates-missing",
      severity: "critical",
      title: "Service dates incomplete",
      why: "Without service-start and service-end the VA can't establish basic eligibility or compute the BDD / FDC eligibility window.",
      fix: "Fill in BOTH the service-start and service-end dates from your DD-214.",
    });
  } else {
    const diffDays = Math.floor((endD - startD) / (1000 * 60 * 60 * 24));
    if (diffDays < MIN_SERVICE_DAYS) {
      add(findings, {
        id: "service-dates-inverted",
        severity: "critical",
        title: "Service end date is before service start",
        why: "Negative service length is physically impossible — VA will reject the 526EZ on intake.",
        fix: "Re-enter the service-start and service-end dates from your DD-214 in the correct order.",
      });
    } else if (diffDays > MAX_SERVICE_DAYS) {
      add(findings, {
        id: "service-dates-too-long",
        severity: "confusion",
        title: `Service length is suspiciously long (${Math.floor(diffDays / 365)} years)`,
        why: "Most veterans serve under 30 years. A multi-decade entry is usually a typo (e.g. wrong century) and VA will flag it.",
        fix: "Confirm both dates against your DD-214 — check the year in particular.",
      });
    } else if (startD.getFullYear() < MIN_REASONABLE_YEAR || endD.getFullYear() < MIN_REASONABLE_YEAR) {
      add(findings, {
        id: "service-dates-pre-1940",
        severity: "confusion",
        title: "Service date is before 1940 — likely a typo",
        why: "WW2-era veterans are exceedingly rare for active VA claims. This is almost always a year-typo.",
        fix: "Check the year on your DD-214 and re-enter.",
      });
    } else if (endD > new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000)) {
      add(findings, {
        id: "service-dates-future",
        severity: "confusion",
        title: "Service end date is more than 5 years in the future",
        why: "Distant-future separation dates create downstream date-math errors and confuse BDD eligibility detection.",
        fix: "Re-check the service-end date.",
      });
    } else if (dobD && startD - dobD < 365 * 16 * 24 * 60 * 60 * 1000) {
      // Entered service younger than 16 — almost certainly DOB typo
      add(findings, {
        id: "service-vs-dob",
        severity: "review",
        title: "Service-start date is before age 16",
        why: "Possible DOB / service-start typo — minimum US enlistment age is 17 (with parental consent).",
        fix: "Check your DOB and service-start year for transposed digits.",
      });
    } else {
      add(findings, {
        id: "service-dates-ok",
        severity: "ready",
        title: "Service dates look sensible",
        why: `${Math.floor(diffDays / 365)}y ${Math.floor((diffDays % 365) / 30)}m of total service.`,
        fix: null,
      });
    }
  }

  // -------------------------------------------------------------------------
  // 3. DUPLICATE CLAIMED CONDITIONS — looks for repeats WITHIN the tracker
  //    and WITHIN the 526EZ claimedConditions list. Cross-list mismatch
  //    (same name in both, which is the EXPECTED aligned state) is handled
  //    by crossFormConsistency, not here.
  // -------------------------------------------------------------------------
  const dupTrackerNames = (issues || []).map((i) => norm(i.claimedAs)).filter(Boolean);
  const dupEzNames = (master526ez.claimedConditions || []).map((c) => norm(c.claimedAs)).filter(Boolean);
  const allLists = [
    { name: "Claim Tracker", list: dupTrackerNames },
    { name: "526EZ claimed-conditions", list: dupEzNames },
  ];
  const dupOffenders = new Set();
  for (const { list } of allLists) {
    const counts = new Map();
    list.forEach((n) => counts.set(n, (counts.get(n) || 0) + 1));
    counts.forEach((c, k) => { if (c > 1) dupOffenders.add(k); });
  }
  const duplicates = Array.from(dupOffenders);
  // Cross-list pool used only for the no-duplicates "ready" condition
  const claimedNames = [...dupTrackerNames, ...dupEzNames];
  if (duplicates.length > 0) {
    add(findings, {
      id: "dup-conditions",
      severity: "confusion",
      title: `Duplicate claimed conditions detected: ${duplicates.join(", ")}`,
      why: "VA may consolidate or deny duplicate claims, OR rate the lower of two overlapping ones (anti-pyramiding, 38 CFR § 4.14). Filing the same condition twice slows everything down.",
      fix: "Remove duplicates from either the Claim Tracker or the 526EZ claimed-conditions list. If the conditions are genuinely different (e.g. left vs right), rename them so the difference is explicit.",
    });
  }

  // -------------------------------------------------------------------------
  // 4. GENERIC CONDITION NAMES — need side / joint / level specificity
  // -------------------------------------------------------------------------
  const genericHits = [];
  claimedNames.forEach((name) => {
    GENERIC_CONDITION_PATTERNS.forEach((p) => {
      if (p.rx.test(name)) genericHits.push({ name, issue: p.issue });
    });
  });
  // De-dup by name
  const seenGeneric = new Set();
  const genericUnique = genericHits.filter((h) => {
    const k = norm(h.name);
    if (seenGeneric.has(k)) return false;
    seenGeneric.add(k);
    return true;
  });
  if (genericUnique.length > 0) {
    genericUnique.forEach((g) => {
      add(findings, {
        id: `generic-${norm(g.name).replace(/\s+/g, "-")}`,
        severity: "review",
        title: `"${g.name}" is too generic`,
        why: g.issue,
        fix: `Rename "${g.name}" to include the missing specificity (e.g. "Right shoulder limitation of motion" or "Lumbar strain, lower back").`,
      });
    });
  } else if (claimedNames.length > 0) {
    add(findings, {
      id: "specificity-ok",
      severity: "ready",
      title: "Condition names look specific enough",
      why: "Every claimed condition includes side / joint / spinal-level or a specific diagnosis.",
      fix: null,
    });
  }

  // -------------------------------------------------------------------------
  // 5. NON-MENTAL CONDITIONS ON THE 21-0781 MENTAL-HEALTH FIELD
  // -------------------------------------------------------------------------
  const mhField = form0781.mentalHealthConditions || "";
  if (mhField.trim()) {
    const offenders = NON_MENTAL_DISORDER_PATTERNS.filter((p) => p.rx.test(mhField)).map((p) => p.label);
    if (offenders.length > 0) {
      add(findings, {
        id: "non-mental-in-0781",
        severity: "confusion",
        title: `${offenders.join(" / ")} listed on the 21-0781 mental-health field`,
        why: "VA Form 21-0781 is for PTSD and other mental-health stressor claims (rated under 38 CFR § 4.130). Physical conditions like sleep apnea belong on the 526EZ as separate claimed conditions with their own DBQ and evidence path. Sleep impairment and insomnia ARE mental-health symptoms; sleep apnea is a physical respiratory disorder.",
        fix: `Remove "${offenders.join(", ")}" from the 21-0781 conditions field. Add it as a separate claimed condition in the Claim Tracker (DC 6847 for sleep apnea, DC 6260 for tinnitus, etc.).`,
      });
    }
  }

  // -------------------------------------------------------------------------
  // 6. BDD-REQUIRED EVIDENCE: STRs + SHA Part A
  // -------------------------------------------------------------------------
  const isBdd = /bdd|pre[-\s]?discharge|benefits delivery at discharge/i.test(
    master526ez.claimType || workspace?.claimType || "",
  );
  if (isBdd) {
    const evTitles = (evidence || []).map((e) => `${e.title || ""} ${e.source || ""} ${e.fileName || ""}`).join(" | ").toLowerCase();
    const hasSTR = /\bstr\b|service treatment|treatment record/.test(evTitles);
    const hasSHA = /\bsha\b|separation health|part a|sep[\s-]?health/.test(evTitles);
    if (!hasSTR) {
      add(findings, {
        id: "bdd-missing-str",
        severity: "critical",
        title: "BDD claim is missing Service Treatment Records (STRs)",
        why: "Benefits Delivery at Discharge claims require STRs as the primary evidence base — without them VA cannot complete the pre-discharge exam packet, and the BDD timeline fails.",
        fix: "Upload your STRs under the In-Service Event pillar on at least one condition. Mark the file source as \"STR\" so the audit can recognise it.",
      });
    }
    if (!hasSHA) {
      add(findings, {
        id: "bdd-missing-sha",
        severity: "critical",
        title: "BDD claim is missing the Separation Health Assessment Part A",
        why: "The SHA Part A (Self-Assessment) is required for every BDD claim. Without it the BDD program will reject the packet at intake.",
        fix: "Complete and upload the Separation Health Assessment Part A. The form lives on VA.gov/find-forms; tag the upload's source as \"SHA Part A\" so the audit recognises it.",
      });
    }
    if (hasSTR && hasSHA) {
      add(findings, {
        id: "bdd-evidence-ok",
        severity: "ready",
        title: "BDD evidence requirements met",
        why: "STRs and the Separation Health Assessment Part A are both on file.",
        fix: null,
      });
    }
  }

  // -------------------------------------------------------------------------
  // 7. 21-0781 COMPLETENESS — only audit when the worksheet is in active use
  // -------------------------------------------------------------------------
  const f0781InUse = !!(
    (form0781.mentalHealthConditions || "").trim()
    || (form0781.eventDescription || "").trim()
    || (form0781.currentImpact || "").trim()
  );
  if (f0781InUse) {
    const checks = [
      { field: "eventDescription", label: "Traumatic event description (what happened)" },
      { field: "eventDate",        label: "Event date (when it happened)" },
      { field: "eventLocation",    label: "Event location (where it happened)" },
      { field: "unitAssignment",   label: "Unit / ship at the time" },
      { field: "currentImpact",    label: "Current occupational and social impact" },
    ];
    const missing = checks.filter((c) => !((form0781[c.field] || "").trim()));
    if (missing.length > 0) {
      add(findings, {
        id: "0781-incomplete",
        severity: "critical",
        title: `VA Form 21-0781 is missing ${missing.length} required field${missing.length === 1 ? "" : "s"}`,
        why: "An incomplete 21-0781 cannot establish the stressor. VA can either reject the stressor or deny the mental-health claim outright for lack of corroboration.",
        fix: `Complete: ${missing.map((m) => m.label).join("; ")}. The narrative should cover: what happened, when, where, unit/ship, who can verify, symptoms that started afterward, and current occupational + social impact.`,
      });
    } else {
      add(findings, {
        id: "0781-complete",
        severity: "ready",
        title: "VA Form 21-0781 fully populated",
        why: "Every required stressor field is filled.",
        fix: null,
      });
    }
  }

  // -------------------------------------------------------------------------
  // 8. CROSS-FORM CONSISTENCY — "you said X here but Y over there" checks.
  //    Implemented in a separate module so the rules can evolve independently
  //    of the static per-form checks above. Same finding shape.
  // -------------------------------------------------------------------------
  const crossFindings = runCrossFormAudit({ master526ez, form0781, issues, statements, workspace });
  crossFindings.forEach((f) => add(findings, f));

  // -------------------------------------------------------------------------
  // Sort & return
  // -------------------------------------------------------------------------
  findings.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  // Top-line aggregate for the panel header
  const counts = { critical: 0, confusion: 0, review: 0, ready: 0 };
  findings.forEach((f) => { counts[f.severity] += 1; });
  let overall;
  if (counts.critical > 0) overall = "critical";
  else if (counts.confusion > 0) overall = "confusion";
  else if (counts.review > 0) overall = "review";
  else overall = "ready";

  return { findings, counts, overall };
}
